import React from 'react';
import PropTypes from 'prop-types';
import { Alert } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import Button from '../Button/Button';
import { useModalContext } from './Modal';

/**
 * Modal Footer Component
 *
 * Standardized modal footer with flexible button configuration and optional alert
 * By default, includes a Close button. Set showDefaultClose={false} to hide it.
 *
 * @example
 * // Default: Shows Close button automatically (right-aligned)
 * <Modal.Footer />
 *
 * @example
 * // Custom buttons with alignment
 * <Modal.Footer
 *   alignment="space-between"
 *   buttons={[
 *     { key: 'delete', label: 'Delete', variant: 'danger' },
 *     { key: 'save', label: 'Save', variant: 'primary',
 *       hoverColor: '#fad133', onClick: handleSave, loading: true }
 *   ]}
 * />
 *
 * @example
 * // Hide default Close button
 * <Modal.Footer showDefaultClose={false} buttons={[...]} />
 */
const ModalFooter = ({
    buttons = [],
    alert,
    showDefaultClose = true,
    alignment, // New optional prop
    children,
    className = '',
    style = {},
    ...props
}) => {
    // Get modal context
    const { onClose } = useModalContext();

    // If children provided, use custom footer
    if (children) {
        return (
            <div className={`ds-modal-footer ${className}`} style={style} {...props}>
                {children}
            </div>
        );
    }

    // Add default Close button if enabled and not already provided
    // Check for close, cancel, or any button that calls onClose
    const hasCloseButton = buttons.some(btn =>
        btn.key === 'close' ||
        btn.key === 'cancel' ||
        btn.onClick === onClose
    );
    const finalButtons = showDefaultClose && !hasCloseButton
        ? [
            {
                key: 'close',
                label: 'Close',
                variant: 'secondary',
                icon: <CloseOutlined />,
                hoverColor: '#FD3635',
                onClick: onClose
            },
            ...buttons
        ]
        : buttons;

    // Build alignment style (if provided)
    const alignmentStyle = alignment ? { justifyContent: alignment } : {};
    const footerStyle = { ...style, ...alignmentStyle };

    // Render buttons from configuration
    return (
        <div className={`ds-modal-footer ${className}`} style={footerStyle} {...props}>
            {alert && (
                <div className="ds-modal-footer__alert">
                    <Alert
                        message={alert.message}
                        type={alert.type || 'info'}
                        showIcon={alert.showIcon !== false}
                        size="small"
                        style={{ fontSize: '12px', ...alert.style }}
                    />
                </div>
            )}
            <div className="ds-modal-footer__buttons">
            {finalButtons.map((buttonConfig) => {
                const {
                    key,
                    label,
                    variant = 'secondary',
                    icon,
                    onClick,
                    loading = false,
                    disabled = false,
                    hoverColor,
                    className: buttonClassName = '',
                    ...rest
                } = buttonConfig;

                return (
                    <Button
                        key={key}
                        variant={variant}
                        icon={icon}
                        onClick={onClick}
                        loading={loading}
                        disabled={disabled}
                        hoverColor={hoverColor}
                        className={buttonClassName}
                        {...rest}
                    >
                        {label}
                    </Button>
                );
            })}
            </div>
        </div>
    );
};

ModalFooter.propTypes = {
    /** Array of button configurations */
    buttons: PropTypes.arrayOf(
        PropTypes.shape({
            /** Unique key for button */
            key: PropTypes.string.isRequired,

            /** Button label */
            label: PropTypes.node.isRequired,

            /** Button variant */
            variant: PropTypes.oneOf(['primary', 'secondary', 'danger']),

            /** Icon element */
            icon: PropTypes.node,

            /** Click handler */
            onClick: PropTypes.func,

            /** Loading state */
            loading: PropTypes.bool,

            /** Disabled state */
            disabled: PropTypes.bool,

            /** Custom hover color */
            hoverColor: PropTypes.string,

            /** Additional CSS classes */
            className: PropTypes.string
        })
    ),

    /** Alert configuration { message, type, showIcon, style } */
    alert: PropTypes.shape({
        message: PropTypes.node.isRequired,
        type: PropTypes.oneOf(['success', 'info', 'warning', 'error']),
        showIcon: PropTypes.bool,
        style: PropTypes.object
    }),

    /** Show default Close button (default: true) */
    showDefaultClose: PropTypes.bool,

    /**
     * Button alignment option
     * - undefined: Default flex-end (right-aligned)
     * - 'flex-start': Left-aligned
     * - 'flex-end': Right-aligned
     * - 'center': Center-aligned
     * - 'space-between': Space between buttons
     * - 'space-around': Space around buttons
     */
    alignment: PropTypes.oneOf([
        'flex-start',
        'flex-end',
        'center',
        'space-between',
        'space-around',
        'space-evenly'
    ]),

    /** Custom footer content (overrides buttons) */
    children: PropTypes.node,

    /** Additional CSS classes */
    className: PropTypes.string,

    /** Inline styles */
    style: PropTypes.object
};

export default ModalFooter;
